'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';

export default function FooterSection({ config, navbarConfig, theme }) {
  const { text, backgroundColor, textColor, emailPlaceholder } = config;
  const logo = navbarConfig?.logo;
  const logoText = navbarConfig?.logoText || 'Padami';
  const [email, setEmail] = useState('');

  const handleSubscribe = (e) => {
    e.preventDefault();
    console.log('Subscribed with email:', email);
    setEmail('');
  };

  const footerLinks = {
    quickLinks: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms Of Use', href: '/terms-of-use' },
      { label: 'FAQ', href: '/faq' },
      { label: 'Contact', href: '/contact' }
    ],
    account: [
      { label: 'My Account', href: '/account' },
      { label: 'Login / Register', href: '/login' },
      { label: 'Cart', href: '/cart' },
      { label: 'Wishlist', href: '/wishlist' },
      { label: 'Shop', href: '/shop' }
    ]
  };

  const features = [
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 12 20 22 4 22 4 12"></polyline>
          <rect x="2" y="7" width="20" height="5"></rect>
          <line x1="12" y1="22" x2="12" y2="7"></line>
          <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"></path>
          <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"></path>
        </svg>
      ),
      title: 'Personalized Gifts'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="3" width="15" height="13"></rect>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
          <circle cx="5.5" cy="18.5" r="2.5"></circle>
          <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
      ),
      title: 'Delivered safely across India'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
          <line x1="1" y1="10" x2="23" y2="10"></line>
        </svg>
      ),
      title: 'Safe & trusted payment gateway'
    },
    {
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
        </svg>
      ),
      title: '24/7 Customer Support'
    }
  ];

  return (
    <div className="footer-container">
      <footer
        style={{
          backgroundColor: theme.variant === 'modern' ? '#33022F' : theme.variant === 'elegant' ? '#3B2414' : (backgroundColor || theme.secondaryColor),
          backgroundImage: theme.variant === 'modern' ? "url('/assets/images/modernHeader.jpg')" : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: (theme.variant === 'modern' || theme.variant === 'elegant') ? '#FFFFFF' : (textColor || theme.primaryColor),
          padding: '4rem 0 3rem'
        }}
        className="footer-main"
      >
        <div className="container">
          <div className="footer-grid">
            {/* Column 1: Logo & Slogan */}
            <div className="footer-column brand-column">
              <div className="footer-logo">
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                  {logo && (
                    <Image 
                      src={(theme.variant === 'modern' || theme.variant === 'elegant') ? '/assets/Modernlogo.svg' : logo} 
                      alt="Logo" 
                      width={45} 
                      height={45} 
                      style={{ objectFit: 'contain' }} 
                    />
                  )}
                  <span style={{ 
                    fontFamily: theme.logoTextFontFamily || 'Elsie Swash Caps, serif', 
                    fontSize: '2.2rem', 
                    fontWeight: theme.logoTextFontWeight || '900',
                    color: (theme.variant === 'modern' || theme.variant === 'elegant') ? '#D4AF37' : (theme.logoTextColor || theme.primaryColor),
                    lineHeight: 1
                  }}>
                    {logoText}
                  </span>
                </div>
              </div>
              <p className="footer-slogan" style={{ fontSize: '1.25rem', fontWeight: '500', opacity: '0.8' }}>
                Every gift begins with a memory.<br />
                We turn it into something truly unforgettable.
              </p>
              
              <div className="newsletter-section">
                <p className="newsletter-text" style={{ 
                  color: theme.variant === 'modern' ? '#D4AF37' : 'inherit',
                  fontSize: theme.variant === 'modern' ? '1.5rem' : '1rem'
                }}>
                  Get 10% off your first order
                </p>
                <form onSubmit={handleSubscribe} className="newsletter-form">
                  <input
                    type="email"
                    className={theme.variant === 'modern' ? 'modern-input' : ''}
                    placeholder={emailPlaceholder || 'Enter your email'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button 
                    type="submit" 
                    className="newsletter-btn"
                    style={{ color: theme.variant === 'modern' ? '#D4AF37' : 'inherit' }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                  </button>
                </form>
              </div>
            </div>

            {/* Column 2: Quick Links */}
            <div className="footer-column">
              <h4 className="footer-title">Quick Link</h4>
              <ul className="footer-list">
                {footerLinks.quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link href={link.href}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 3: Account */}
            <div className="footer-column">
              <h4 className="footer-title">Account</h4>
              <ul className="footer-list">
                {footerLinks.account.map((link, index) => (
                  <li key={index}>
                    <Link href={link.href}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Column 4: Support */}
            <div className="footer-column">
              <h4 className="footer-title">Support</h4>
              <ul className="footer-list support-list">
                <li>
                  2/410 , Sedan Thottam (via) , Thottipalayam,<br />
                  Karavalli Madhapur (po),<br />
                  Somanur, Coimbatore –<br />
                  641668<br />
                  Tamil Nadu, India
                </li>
                <li>
                  <a href="mailto:padamiapp@gmail.com">padamiapp@gmail.com</a>
                </li>
                <li>
                  <a href="tel:+918248130846">+91 82481 30846</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      {/* Features Bar */}
      <div className="features-bar">
        <div className="container">
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-item">
                <div className="feature-icon" style={{ color: theme.primaryColor }}>
                  {feature.icon}
                </div>
                <span className="feature-title">{feature.title}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .footer-container {
          width: 100%;
          height: 100%;
          font-family: inherit;
        }
        .footer-main {
          width: 100%;
          height: 100%;
        }
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1.5rem;
        }
        .footer-grid {
          display: grid;
          grid-template-columns: 1.5fr 0.8fr 0.8fr 1.2fr;
          gap: 4rem;
        }
        .footer-column {
          display: flex;
          flex-direction: column;
        }
        .footer-logo {
          margin-bottom: 2rem;
        }
        .footer-slogan {
          font-size: 1.15rem;
          line-height: 1.6;
          margin-bottom: 2.5rem;
          opacity: 0.9;
          color: inherit;
        }
        .newsletter-section {
          margin-top: auto;
        }
        .newsletter-text {
          font-weight: 600;
          margin-bottom: 1rem;
          font-size: 1rem;
          color: inherit;
        }
        .newsletter-form {
          position: relative;
          display: flex;
          max-width: 320px;
          background: rgba(255, 255, 255, 0.4);
          border-radius: 8px;
          border: 1.5px solid rgba(107, 43, 82, 0.2);
          transition: all 0.3s ease;
        }
        .newsletter-form:focus-within {
          border-color: rgba(107, 43, 82, 0.5);
          background: rgba(255, 255, 255, 0.6);
          box-shadow: 0 4px 12px rgba(107, 43, 82, 0.05);
        }
        .newsletter-form input {
          width: 100%;
          padding: 0.85rem 3.5rem 0.85rem 1.25rem;
          background: transparent;
          border: none;
          font-size: 1rem;
          outline: none;
          color: inherit;
          font-family: inherit;
        }
        .modern-input::placeholder {
          color: rgba(255, 255, 255, 0.6) !important;
        }
        .modern-input {
          color: white !important;
        }
        .newsletter-btn {
          position: absolute;
          right: 0.5rem;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          cursor: pointer;
          color: inherit;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 35px;
          height: 35px;
          border-radius: 50%;
          transition: all 0.2s ease;
        }
        .newsletter-btn:hover {
          background: rgba(107, 43, 82, 0.1);
          transform: translateY(-50%) scale(1.1);
        }
        .footer-title {
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 2.5rem;
          position: relative;
          color: inherit;
        }
        .footer-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .footer-list li {
          margin-bottom: 1.15rem;
        }
        .footer-list :global(a),
        .footer-list :global(a:visited) {
          text-decoration: none;
          color: inherit;
          opacity: 0.85;
          transition: all 0.2s ease;
          font-size: 1rem;
        }
        .footer-list :global(a:hover) {
          opacity: 1;
          padding-left: 5px;
        }
        .support-list li {
          font-size: 1rem;
          line-height: 1.7;
          opacity: 0.9;
          margin-bottom: 1.5rem;
        }
        .support-list :global(a) {
          opacity: 1;
          font-weight: 600;
        }

        /* Features Bar */
        .features-bar {
          background: #fff;
          padding: 3.5rem 0;
          border-top: 1px solid rgba(0,0,0,0.03);
        }
        .features-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2.5rem;
        }
        .feature-item {
          display: flex;
          align-items: center;
          gap: 1.25rem;
          transition: transform 0.3s ease;
        }
        .feature-item:hover {
          transform: translateY(-5px);
        }
        .feature-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          width: 44px;
          height: 44px;
          background: rgba(107, 43, 82, 0.05);
          border-radius: 12px;
          transition: all 0.3s ease;
        }
        .feature-item:hover .feature-icon {
          background: rgba(107, 43, 82, 0.1);
        }
        .feature-title {
          font-size: 1rem;
          font-weight: 600;
          color: #2d2d2d;
          line-height: 1.4;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .footer-grid {
            gap: 2rem;
          }
        }
        @media (max-width: 1024px) {
          .footer-grid {
            grid-template-columns: 1fr 1fr;
            gap: 3.5rem;
          }
          .features-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 3rem;
          }
        }
        @media (max-width: 640px) {
          .footer-grid {
            grid-template-columns: 1fr;
            gap: 3.5rem;
            text-align: center;
          }
          .footer-column {
            align-items: center;
          }
          .newsletter-form {
            margin: 0 auto;
          }
          .features-grid {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
          .feature-item {
            flex-direction: column;
            text-align: center;
          }
          .footer-title {
            margin-bottom: 1.5rem;
          }
          .footer-logo {
            margin-bottom: 1.5rem;
          }
          .footer-slogan {
            margin-bottom: 2rem;
          }
        }
      `}</style>
    </div>
  );
}
